// ABOUTME: Loads page overrides for the current route and applies them to the DOM for every visitor.
// ABOUTME: Re-applies on route changes and on late content mutations; failures are silent by design.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { applyOverrides, resetOverrides } from '@/lib/live-editor/apply';
import { routePatternOf, type PageOverride } from '@/lib/live-editor/types';

export const pageOverridesQueryKey = (pattern: string) => ['page-overrides', pattern];

const fetchOverrides = async (pattern: string): Promise<PageOverride[]> => {
  const { data, error } = await supabase
    .from('page_overrides')
    .select('id, route_pattern, selector, kind, patch, position, sort_order')
    .eq('route_pattern', pattern)
    .order('sort_order');
  if (error) throw error;
  return (data ?? []) as unknown as PageOverride[];
};

/** Query hook shared by the runtime applier and the live editor overlay. */
export const usePageOverridesQuery = (pattern: string) =>
  useQuery({
    queryKey: pageOverridesQueryKey(pattern),
    queryFn: () => fetchOverrides(pattern),
    staleTime: 2 * 60 * 1000,
  });

/**
 * Applies the stored overrides for the current route. Mounted once in the app
 * layout so customisations show up for all users, not just admins.
 */
export const usePageOverrides = (enabled = true) => {
  const location = useLocation();
  const pattern = routePatternOf(location.pathname);
  const { data } = usePageOverridesQuery(pattern);
  const overrides = data ?? [];
  const applying = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (overrides.length === 0) {
      resetOverrides();
      return;
    }

    let timer: number | undefined;
    let observer: MutationObserver | undefined;

    const run = () => {
      applying.current = true;
      observer?.disconnect();
      try {
        applyOverrides(overrides);
      } catch {
        // Never surface override failures to visitors
      }
      observer?.observe(document.body, { childList: true, subtree: true });
      applying.current = false;
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 120);
    };

    observer = new MutationObserver(() => {
      if (!applying.current) schedule();
    });
    schedule();

    return () => {
      window.clearTimeout(timer);
      observer?.disconnect();
      resetOverrides();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pattern, JSON.stringify(overrides)]);

  return { pattern, overrides };
};
